import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Maximize2, MessageCircle, SquarePen, X } from 'lucide-react';
import { memberClubsOf } from '../mobile/memberData';
import { clubGroupsFor, isMessagingDisabled, messagingPlayers } from '../../engine/messagingGroups';
import { MessagingOffCard, MessagingOffLink } from './MessagingSwitch';
import { ConversationAvatar } from '../mobile/screens/MobileMessages';
import {
  CLUB_SENDER_ID,
  conversationById,
  conversationTitle,
  conversationsFor,
  previewOf,
  timeLabel,
  unreadCount,
} from '../../engine/messaging';
import { nameSourceFor } from '../mobile/messagingUi';
import { initials } from '../mobile/mobileUi';
import type { MessagingApi } from '../../state/useMessaging';
import type { Conversation } from '../../engine/messaging';
import { ClubThread, MemberPickModal } from './MessagesView';

export interface MessagesDockProps {
  messaging: MessagingApi;
  effectivePlayerDB: any[];
  clubInfo: any;
  open: boolean;
  onToggle: () => void;
  /** Go to the full «Meldinger» view. */
  onFullscreen: () => void;
  /** Injectable clock for deterministic renders. */
  now?: number;
}

// Messenger-style dock for the club portal: a panel that drops down under the
// header's Meldinger icon (top-right) — inbox list incl. the automatic club
// groups, then a thread — with a shortcut to the full-screen «Meldinger» view.
export function MessagesDock({
  messaging,
  effectivePlayerDB,
  clubInfo,
  open,
  onToggle,
  onFullscreen,
  now,
}: MessagesDockProps) {
  const nowMs = now ?? Date.now();
  const { state } = messaging;
  const me = CLUB_SENDER_ID;
  const names = useMemo(
    () => nameSourceFor(effectivePlayerDB, clubInfo),
    [effectivePlayerDB, clubInfo],
  );
  const [openId, setOpenId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread' | 'groups'>('all');
  const [picking, setPicking] = useState(false);
  const clubOff = isMessagingDisabled(state, me);
  const clubs = useMemo(() => memberClubsOf(clubInfo), [clubInfo]);

  const rows = useMemo(
    () =>
      conversationsFor(state, me).map((c) => ({
        c,
        title: conversationTitle(c, me, names),
        preview: previewOf(state, c, me, names),
        unread: unreadCount(state, c, me),
      })),
    [state, me, names],
  );
  const groupRows = useMemo(
    () =>
      clubGroupsFor(state, clubs, effectivePlayerDB || []).map((c) => ({
        c,
        title: conversationTitle(c, me, names),
        preview: previewOf(state, c, me, names),
        unread: unreadCount(state, c, me),
      })),
    [state, clubs, effectivePlayerDB, me, names],
  );
  const totalUnread = [...rows, ...groupRows].reduce((n, r) => n + r.unread, 0);
  // «Alle» shows automatic groups only once they have messages; «Grupper» shows all.
  const activeGroups = groupRows.filter((r) => r.c.lastMessageAt > 0);
  const listRows = (
    filter === 'groups'
      ? groupRows
      : filter === 'unread'
        ? [...rows, ...activeGroups].filter((r) => r.unread > 0)
        : [...rows, ...activeGroups]
  ).sort((a, b) => b.c.lastMessageAt - a.c.lastMessageAt || a.c.id.localeCompare(b.c.id));
  // Derived groups win over their bare stored record (which has no members/title).
  const openConv = openId
    ? groupRows.find((r) => r.c.id === openId)?.c || conversationById(state, openId) || null
    : null;
  const openRow = async (c: Conversation) => {
    if (c.rule && !conversationById(state, c.id)) await messaging.openDynamic(c.rule);
    setOpenId(c.id);
  };

  // ── A NON-MODAL DIALOG, AND IT BEHAVES LIKE ONE (#900) ─────────────
  //
  // The dock carried `role="dialog"` and nothing else: focus stayed on the
  // header icon when it opened, Escape did nothing, and closing it from its
  // own ✕ dropped focus to the top of the document.
  //
  // It is NOT made modal, and that is deliberate. It does not dim or block
  // the page — it is a messenger panel you keep open while you work, and
  // trapping Tab in it (or locking scroll, as `Dialog` does) would change what
  // it is. A non-modal dialog owes three things instead, which is what this
  // does:
  //
  //   • focus moves INTO it on open, so a screen reader announces it by name
  //     and the next Tab reaches its controls;
  //   • Escape closes it — unless something has already claimed that Escape
  //     with `preventDefault()`: a `Dialog` on top of it («Slå av
  //     meldinger?», «Ny melding») or the emoji picker in a thread;
  //   • closing it hands focus back to whatever opened it, but only when focus
  //     was in the dock or has nowhere to be. A pointer user who has already
  //     clicked into the page behind it keeps the focus they chose.
  const panelRef = useRef<HTMLDivElement | null>(null);
  const onToggleRef = useRef(onToggle);
  onToggleRef.current = onToggle;
  useEffect(() => {
    if (!open) return undefined;
    const panel = panelRef.current;
    const opener = document.activeElement;
    panel?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      onToggleRef.current();
    };
    // ON `window`, IN THE BUBBLE PHASE — the last listener a keypress
    // reaches. A `Dialog` on top of the dock acts in capture, and the emoji
    // picker in a thread on the document; both claim their Escape with
    // `preventDefault()` before this runs, so one Escape closes one thing.
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const now = document.activeElement;
      const lost = !now || now === document.body || !!panel?.contains(now);
      if (lost && opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [open]);

  // An open thread is read for the club — also as new replies land.
  useEffect(() => {
    if (open && openConv && unreadCount(state, openConv, me) > 0) {
      void messaging.read(openConv.id, me);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openConv?.id, state]);

  return (
    <>
      {open && (
        <div
          className="fixed right-4 z-40 card-elev flex flex-col overflow-hidden slide-up"
          style={{
            top: 64,
            width: 380,
            maxWidth: 'calc(100vw - 2rem)',
            height: 'min(640px, calc(100dvh - 8rem))',
          }}
          ref={panelRef}
          role="dialog"
          aria-label="Meldinger"
          tabIndex={-1}
          data-testid="messages-dock"
        >
          {/* Dock header */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-line">
            {openConv ? (
              <button
                onClick={() => setOpenId(null)}
                className="btn-ghost p-1 rounded-full"
                aria-label="Tilbake til samtalene"
              >
                <ArrowLeft size={16} />
              </button>
            ) : (
              <MessageCircle size={16} className="text-ink-2" />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold leading-tight truncate">
                {openConv ? conversationTitle(openConv, me, names) : 'Meldinger'}
              </div>
              <div className="text-[11px] text-ink-3 truncate">
                {openConv
                  ? openConv.kind === 'dynamic'
                    ? `Automatisk gruppe · ${openConv.participantIds.length} medlemmer`
                    : 'Samtale mellom klubben og medlemmet'
                  : totalUnread > 0
                    ? `${totalUnread} ulest${totalUnread === 1 ? '' : 'e'}`
                    : names.clubName}
              </div>
            </div>
            {!openConv && (
              <button
                onClick={() => setPicking(true)}
                className="btn-ghost p-1.5 rounded-full"
                title="Ny melding"
                aria-label="Ny melding"
              >
                <SquarePen size={15} />
              </button>
            )}
            <button
              onClick={onFullscreen}
              className="btn-ghost p-1.5 rounded-full"
              title="Fullskjerm"
              aria-label="Åpne Meldinger i fullskjerm"
            >
              <Maximize2 size={15} />
            </button>
            <button onClick={onToggle} className="btn-ghost p-1.5 rounded-full" aria-label="Lukk">
              <X size={15} />
            </button>
          </div>

          {/* Body */}
          {clubOff ? (
            <MessagingOffCard
              subject="klubben"
              onSet={(d) => messaging.setDisabled(me, d)}
              compact
            />
          ) : openConv ? (
            <ClubThread
              key={openConv.id}
              state={state}
              conversation={openConv}
              names={names}
              nowMs={nowMs}
              compact
              onSend={(text) => messaging.send(openConv.id, me, text)}
            />
          ) : (
            <div className="flex-1 overflow-y-auto divide-y divide-line">
              <div className="flex gap-1 px-3 py-1.5">
                {(
                  [
                    { id: 'all', label: 'Alle' },
                    { id: 'unread', label: 'Uleste' },
                    { id: 'groups', label: 'Grupper' },
                  ] as { id: 'all' | 'unread' | 'groups'; label: string }[]
                ).map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    aria-pressed={filter === f.id}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      filter === f.id
                        ? 'bg-ink text-white border-ink'
                        : 'bg-paper text-ink-2 border-line hover:bg-bg-soft'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              {listRows.length === 0 && (
                <div className="p-6 text-center text-sm text-ink-2">
                  <MessageCircle size={22} className="mx-auto mb-2 text-ink-4" />
                  {filter === 'unread'
                    ? 'Ingenting ulest.'
                    : 'Ingen samtaler ennå. Åpne fullskjerm for «Ny melding» og «Send til alle».'}
                </div>
              )}
              {listRows.map(({ c, title, preview, unread }) => (
                <button
                  key={c.id}
                  onClick={() => void openRow(c)}
                  className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-bg-soft/60 transition-colors"
                >
                  {c.kind === 'dynamic' ? (
                    <ConversationAvatar conversation={c} title={title} size={36} />
                  ) : (
                    <span
                      className="w-9 h-9 rounded-full flex items-center justify-center font-display font-semibold text-xs flex-shrink-0"
                      style={{ background: 'var(--line)', color: 'var(--ink)' }}
                      aria-hidden
                    >
                      {initials(title)}
                    </span>
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className={`text-sm truncate ${unread ? 'font-bold' : 'font-medium'}`}>
                        {title}
                      </span>
                      <span className="font-mono text-[10px] text-ink-3 flex-shrink-0">
                        {timeLabel(c.lastMessageAt, nowMs)}
                      </span>
                    </span>
                    <span className="flex items-center justify-between gap-2">
                      <span className={`text-xs truncate ${unread ? 'text-ink' : 'text-ink-3'}`}>
                        {c.kind === 'dynamic' && !c.lastMessageAt
                          ? `${c.participantIds.length} medlemmer · automatisk gruppe`
                          : preview}
                      </span>
                      {unread > 0 && (
                        <span
                          className="font-mono text-[10px] font-bold px-1.5 rounded-full flex-shrink-0"
                          style={{ background: 'var(--lime)', color: 'var(--ink)' }}
                        >
                          {unread}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {!clubOff && !openConv && (
            <div className="px-3 py-2 border-t border-line text-center">
              <MessagingOffLink subject="klubben" onSet={(d) => messaging.setDisabled(me, d)} />
            </div>
          )}
        </div>
      )}
      {open && picking && (
        <MemberPickModal
          players={messagingPlayers(state, effectivePlayerDB)}
          onClose={() => setPicking(false)}
          onPick={async (id) => {
            const conv = await messaging.openClubConversation(id);
            setPicking(false);
            setOpenId(conv.id);
          }}
        />
      )}
    </>
  );
}
